export const DEFAULT_BACKUP_RETENTION_POLICY = {
  daily: 7,
  weekly: 4,
  monthly: 12,
};

const isValidTimestamp = (timestamp) => Number.isFinite(Number(timestamp));

const getDateParts = (timestamp) => {
  const date = new Date(Number(timestamp));
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    date: date.getDate(),
    day: date.getDay(),
  };
};

const pad = (value) => String(value).padStart(2, '0');

const getDayKey = (timestamp) => {
  const parts = getDateParts(timestamp);
  return `${parts.year}-${pad(parts.month)}-${pad(parts.date)}`;
};

const getMonthKey = (timestamp) => {
  const parts = getDateParts(timestamp);
  return `${parts.year}-${pad(parts.month)}`;
};

const getYearKey = (timestamp) => {
  const parts = getDateParts(timestamp);
  return `${parts.year}`;
};

const getWeekKey = (timestamp) => {
  const date = new Date(Number(timestamp));
  const parts = getDateParts(timestamp);
  const daysSinceMonday = (parts.day + 6) % 7;
  date.setDate(parts.date - daysSinceMonday);
  const weekStartParts = getDateParts(date.getTime());
  return `${weekStartParts.year}-${pad(weekStartParts.month)}-${pad(weekStartParts.date)}`;
};

const newestByBucket = (backups, getBucketKey, limit = Infinity) => {
  const buckets = new Map();

  backups.forEach((backup) => {
    const bucketKey = getBucketKey(backup.createdAt);
    const existing = buckets.get(bucketKey);
    if (!existing || (backup.createdAt || 0) > (existing.createdAt || 0)) {
      buckets.set(bucketKey, backup);
    }
  });

  return [...buckets.values()]
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    .slice(0, limit);
};

export const getBackupRetentionPlan = (
  backups,
  policy = DEFAULT_BACKUP_RETENTION_POLICY
) => {
  const automaticBackups = (backups || [])
    .filter((backup) => backup?.id && backup?.type === 'automatic' && isValidTimestamp(backup.createdAt))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  const retainedIds = new Set();

  [
    ...newestByBucket(automaticBackups, getDayKey, policy.daily),
    ...newestByBucket(automaticBackups, getWeekKey, policy.weekly),
    ...newestByBucket(automaticBackups, getMonthKey, policy.monthly),
    ...newestByBucket(automaticBackups, getYearKey),
  ].forEach((backup) => {
    retainedIds.add(backup.id);
  });

  const deleteIds = automaticBackups
    .filter((backup) => !retainedIds.has(backup.id))
    .map((backup) => backup.id);

  return {
    retainedIds: [...retainedIds],
    deleteIds,
  };
};
